import { useCallback, useState } from 'react';

import { MqSwitch } from '@fhs/ui';

import { GiftCardActionSheet } from '../../components/gift-cards-action-sheet';
import { CloseConfirmationModal } from '../../components/gift-cards-action-sheet/close-confirmation-modal';
import { useGiftCardsActionSheet } from '../../components/gift-cards-action-sheet/use-gift-cards-action-sheet';
import { GiftCardsTransactionHistoryActionSheet } from '../../components/gift-cards-transaction-history-action-sheet';
import { useLazyGetGiftCardDataQuery } from '../../queries/use-get-gift-card-data';
import { useGiftCardContext } from '../../state/gift-card';

import { GiftCardsDesktop } from './gift-cards-desktop';
import { GiftCardsMobile } from './gift-cards-mobile';

export function GiftCards() {
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);

  const {
    actionSheetOpen: addGiftCardActionSheetOpen,
    handleActionSheetOpen: handleAddGiftCardActionSheetOpen,
    handleRequestClose: handleAddGiftCardRequestClose,
    currentStep: currentAddGiftCardActionSheetStep,
    setCurrentStep: setCurrentAddGiftCardActionSheetStep,
    headerTitle: addGiftCardActionSheetHeaderTitle,
    closeConfirmationOpen: addGiftCardCloseConfirmationOpen,
    handleConfirmStay: handleAddGiftCardConfirmStay,
    handleConfirmLeave: handleAddGiftCardConfirmLeave,
  } = useGiftCardsActionSheet();

  const {
    actionSheetOpen: checkBalanceActionSheetOpen,
    handleActionSheetOpen: handleCheckBalanceActionSheetOpen,
    handleRequestClose: handleCheckBalanceRequestClose,
    currentStep: currentCheckBalanceActionSheetStep,
    setCurrentStep: setCurrentCheckBalanceActionSheetStep,
    headerTitle: checkBalanceActionSheetHeaderTitle,
    closeConfirmationOpen: checkBalanceCloseConfirmationOpen,
    handleConfirmStay: handleCheckBalanceConfirmStay,
    handleConfirmLeave: handleCheckBalanceConfirmLeave,
  } = useGiftCardsActionSheet({ isCheckBalanceFlow: true });

  const { lastDigits, isDigitalBalanceLoading, currentBalance, handleBalanceUpdate, handleError } =
    useGiftCardContext();

  const [isCardNumberRedacted, setIsCardNumberRedacted] = useState(true);

  const { query, doQuery: triggerGiftCardDataQuery } = useLazyGetGiftCardDataQuery();
  const cardDataLoading = query.isLoading || query.isFetching;
  const cardNumber = query.data?.giftCardData?.cardNumber;
  const pin = query.data?.giftCardData?.pin;

  const onPressShowCardNumber = useCallback(async () => {
    triggerGiftCardDataQuery();
    setIsCardNumberRedacted(redacted => !redacted);
  }, [triggerGiftCardDataQuery]);

  return (
    <>
      <MqSwitch
        $ltDesktop={() => (
          <GiftCardsMobile
            onAddGiftCardPress={handleAddGiftCardActionSheetOpen}
            onCheckBalancePress={handleCheckBalanceActionSheetOpen}
            lastDigits={lastDigits}
            onPressShowCardNumber={onPressShowCardNumber}
            isCardNumberRedacted={isCardNumberRedacted}
            isCardNumberLoading={cardDataLoading}
            cardNumber={cardNumber}
            pin={pin}
            onSeeHistoryPress={() => setShowTransactionHistory(true)}
            isDigitalBalanceLoading={isDigitalBalanceLoading}
          />
        )}
        $gteDesktop={() => (
          <GiftCardsDesktop
            onAddGiftCardPress={handleAddGiftCardActionSheetOpen}
            onCheckBalancePress={handleCheckBalanceActionSheetOpen}
            lastDigits={lastDigits}
            onPressShowCardNumber={onPressShowCardNumber}
            isCardNumberRedacted={isCardNumberRedacted}
            isCardNumberLoading={cardDataLoading}
            cardNumber={cardNumber}
            pin={pin}
            onSeeHistoryPress={() => setShowTransactionHistory(true)}
            isDigitalBalanceLoading={isDigitalBalanceLoading}
          />
        )}
      />
      <GiftCardActionSheet
        headerTitle={addGiftCardActionSheetHeaderTitle}
        isVisible={addGiftCardActionSheetOpen}
        currentStep={currentAddGiftCardActionSheetStep}
        currentDigitalBalance={currentBalance}
        handleBalanceUpdate={handleBalanceUpdate}
        handleError={handleError}
        setCurrentStep={setCurrentAddGiftCardActionSheetStep}
        onClose={handleAddGiftCardRequestClose}
      />
      <GiftCardActionSheet
        headerTitle={checkBalanceActionSheetHeaderTitle}
        isVisible={checkBalanceActionSheetOpen}
        isCheckBalanceFlow
        currentStep={currentCheckBalanceActionSheetStep}
        currentDigitalBalance={currentBalance}
        handleBalanceUpdate={handleBalanceUpdate}
        handleError={handleError}
        setCurrentStep={setCurrentCheckBalanceActionSheetStep}
        onClose={handleCheckBalanceRequestClose}
      />
      {showTransactionHistory && (
        <GiftCardsTransactionHistoryActionSheet
          isVisible
          onClose={() => setShowTransactionHistory(false)}
        />
      )}
      {/* Confirmation modals rendered at parent level to avoid nested Modal issue on iOS */}
      <CloseConfirmationModal
        isOpen={addGiftCardCloseConfirmationOpen}
        onConfirm={handleAddGiftCardConfirmStay}
        onDismiss={handleAddGiftCardConfirmLeave}
      />
      <CloseConfirmationModal
        isOpen={checkBalanceCloseConfirmationOpen}
        onConfirm={handleCheckBalanceConfirmStay}
        onDismiss={handleCheckBalanceConfirmLeave}
      />
    </>
  );
}
